import React from 'react'
import GlassPanel from '../UI/GlassPanel'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const Metric = ({ label, value, sub }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
    <div className="text-lg font-bold text-white tabular-nums leading-tight">{value}</div>
    <div className="text-xs text-gray-300">{label}</div>
    {sub && <div className="text-xs text-gray-500 truncate">{sub}</div>}
  </div>
)

/**
 * The deeper cuts: rhythm, coverage and loyalty.
 *
 * Everything here is derived from data already in the passport — no extra data
 * entry — which is why it stays correct as older events are backfilled.
 */
const DeepStatsSection = ({ deep = {}, lineupCoverage = {}, genres = [] }) => {
  const {
    lastEvent, nextEvent, firstEvent,
    averageSetsPerEvent = 0, repeatArtists = 0, repeatRate = 0,
    mostSeenArtist, distinctVenues = 0, venueLoyalty = 0,
    busiestMonth, monthCounts = [], longestStreak = {}
  } = deep

  const { overallPercent = 0, totalSeen = 0, totalOnBills = 0, events: coverageEvents = [] } = lineupCoverage

  const hasAnything = totalOnBills > 0 || repeatArtists > 0 || lastEvent || nextEvent
  if (!hasAnything) return null

  const monthPeak = Math.max(1, ...monthCounts)
  const genreTotal = genres.reduce((total, genre) => total + genre.sets, 0)

  return (
    <GlassPanel className="p-6 mb-8">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-xl font-bold text-white">🔬 Deep stats</h2>
        <p className="text-sm text-gray-400">Everything derived from what's already logged</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Metric
          label="Lineup caught"
          value={`${overallPercent}%`}
          sub={totalOnBills ? `${totalSeen} of ${totalOnBills} sets` : null}
        />
        <Metric label="Sets per event" value={averageSetsPerEvent} />
        <Metric
          label="Repeat artists"
          value={`${repeatRate}%`}
          sub={`${repeatArtists} seen more than once`}
        />
        <Metric
          label="Longest streak"
          value={longestStreak.length ? `${longestStreak.length} yr` : '—'}
          sub={longestStreak.from ? `${longestStreak.from}–${longestStreak.to}` : null}
        />
        <Metric
          label="Most seen"
          value={mostSeenArtist ? `${mostSeenArtist.timesSeen}×` : '—'}
          sub={mostSeenArtist?.name}
        />
        <Metric label="Venues" value={distinctVenues} sub={venueLoyalty ? `${venueLoyalty}% at your top one` : null} />
        <Metric
          label="Since last event"
          value={lastEvent ? `${lastEvent.daysAgo}d` : '—'}
          sub={lastEvent?.eventName}
        />
        <Metric
          label="Until next"
          value={nextEvent ? `${nextEvent.daysAway}d` : '—'}
          sub={nextEvent?.eventName || 'nothing booked'}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* When you go */}
        {busiestMonth && (
          <div>
            <h3 className="text-sm font-medium text-gray-300 mb-2">
              When you go · peak {MONTHS[busiestMonth.month]}
            </h3>
            <div className="flex items-end gap-1 h-20">
              {monthCounts.map((count, index) => (
                <div key={index} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <div
                    className={`w-full rounded-t ${count ? 'bg-gradient-to-t from-purple-600 to-pink-500' : 'bg-white/5'}`}
                    style={{ height: `${count ? Math.max(10, (count / monthPeak) * 100) : 4}%` }}
                    title={`${MONTHS[index]}: ${count} event${count === 1 ? '' : 's'}`}
                  />
                  <span className="text-[9px] text-gray-500">{MONTHS[index][0]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Genre mix */}
        {genres.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-300 mb-2">Genre mix</h3>
            <div className="space-y-1.5">
              {genres.slice(0, 6).map(genre => (
                <div key={genre.genre}>
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-gray-200 truncate">{genre.genre}</span>
                    <span className="text-gray-500 flex-shrink-0">
                      {Math.round((genre.sets / genreTotal) * 100)}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                      style={{ width: `${(genre.sets / genreTotal) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Per-event coverage */}
      {coverageEvents.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-medium text-gray-300 mb-2">
            How much of each bill you caught
          </h3>
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {coverageEvents.map(item => (
              <div key={item.eventId} className="flex items-center gap-3">
                <span className="text-sm text-gray-200 truncate w-40 flex-shrink-0">
                  {item.eventName}
                </span>
                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-emerald-400"
                    style={{ width: `${item.percent}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 tabular-nums w-20 text-right flex-shrink-0">
                  {item.seen}/{item.total}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {firstEvent && (
        <p className="text-xs text-gray-500 mt-5">
          Passport starts {new Date(firstEvent.date).toLocaleDateString('en-US', {
            month: 'long', day: 'numeric', year: 'numeric'
          })} · {firstEvent.eventName}
        </p>
      )}
    </GlassPanel>
  )
}

export default DeepStatsSection
